.UnderlineWrapper {
  display: flex;
  padding-top: var(--base-size-8);
  /* stylelint-disable-next-line primer/spacing */
  padding-inline: var(--stack-padding-normal);
  justify-content: flex-start;
  align-items: flex-start;

  /* make space for the underline */
  min-height: var(--control-xlarge-size);

  /* using a box-shadow instead of a border to accommodate 'overflow-y: hidden' on UnderlinePanels */
  /* stylelint-disable-next-line primer/box-shadow */
  box-shadow: inset 0 -1px var(--borderColor-muted);

  &[data-variant='flush'] {
    /* stylelint-disable-next-line primer/spacing */
    padding-inline: unset;
  }

  &[data-overflow-mode='wrap'] {
    /* Wrap items onto hidden next lines */
    overflow: hidden;
    max-height: var(--control-xlarge-size);

    /* Pin the vertical box model so external border/padding overrides can't change the height budget and incorrectly trigger the overflow "more" menu */
    box-sizing: border-box;
    border-block: 0 !important;
    padding-block: var(--base-size-8) 0 !important;

    .UnderlineItemList {
      flex-grow: 1;
      flex-wrap: wrap;
      overflow: hidden;
    }
  }
}

.UnderlineItemList {
  position: relative;
  display: flex;
  padding: 0;
  margin: 0;
  white-space: nowrap;
  list-style: none;
  align-items: center;
  gap: var(--stack-gap-condensed);

  /* Isolate the ResizeObserver-driven item swapping (UnderlineNav overflow menu / UnderlinePanels)
  so it can't invalidate ancestor layout. Safe because UnderlineNav's overflow menu is rendered via ActionMenu.Overlay
  (portal by default), and layout containment does not clip. */
  contain: layout;
}

.UnderlineItem {
  /* underline tab specific styles */
  position: relative;
  display: inline-flex;
  font: inherit;
  font-size: var(--text-body-size-medium);
  line-height: var(--text-body-lineHeight-medium, 1.4285);
  color: var(--fgColor-default);
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  background-color: transparent;
  border: 0;
  border-radius: var(--borderRadius-medium);
  max-width: 100%;
  margin-bottom: var(--base-size-8);
  height: var(--base-size-32);

  /* button resets */
  appearance: none;
  padding-inline: var(--base-size-8);
  padding-block: var(--base-size-6);
  align-items: center;

  @media (hover: hover) {
    &:hover {
      text-decoration: none;
      background-color: var(--bgColor-neutral-muted);
      transition: background-color 0.12s ease-out;
    }
  }
}

.UnderlineItem:focus {
  outline: 2px solid transparent;
  /* stylelint-disable-next-line primer/box-shadow */
  box-shadow: inset 0 0 0 2px var(--fgColor-accent);

  /* where focus-visible is supported, remove the focus box-shadow */
  &:not(:focus-visible) {
    box-shadow: none;
  }
}

.UnderlineItem:focus-visible {
  outline: 2px solid transparent;
  /* stylelint-disable-next-line primer/box-shadow */
  box-shadow: inset 0 0 0 2px var(--fgColor-accent);
}

/* renders a visibly hidden "copy" of the label in bold, reserving box space for when label becomes bold on selected */
.UnderlineItem [data-content]::before {
  display: block;
  height: 0;
  font-weight: var(--base-text-weight-semibold);
  white-space: nowrap;
  visibility: hidden;
  content: attr(data-content);
}

.UnderlineItem [data-component='icon'] {
  display: inline-flex;
  color: var(--fgColor-muted);
  align-items: center;
  margin-inline-end: var(--base-size-8);
}

/* Truncate items if necessary (should only ever apply to the last item after everything else overflows) */
.UnderlineItem [data-component='text'] {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.UnderlineItem [data-component='counter'] {
  margin-inline-start: var(--base-size-8);
  display: flex;
  align-items: center;
}

.UnderlineItem::after {
  position: absolute;
  inset: auto 0 0;
  margin-bottom: calc(-1 * var(--base-size-8));
  height: 2px;
  pointer-events: none;
  content: '';
  background-color: transparent;
}

.UnderlineItem[aria-current]:not([aria-current='false']) [data-component='text'],
.UnderlineItem[aria-selected='true'] [data-component='text'] {
  font-weight: var(--base-text-weight-semibold);
}

.UnderlineItem[aria-current]:not([aria-current='false'])::after,
.UnderlineItem[aria-selected='true']::after {
  /* stylelint-disable-next-line primer/colors */
  background-color: var(--underlineNav-borderColor-active, var(--color-primer-border-active, #fd8c73));

  @media (forced-colors: active) {
    /* Support for Window Force Color Mode https://learn.microsoft.com/en-us/fluent-ui/web-components/design-system/high-contrast */
    background-color: LinkText;
  }
}

.LoadingCounter {
  display: inline-block;
  width: 1.5rem;
  height: 1rem;
  /* 16px */
  background-color: var(--bgColor-neutral-muted);
  border-color: var(--borderColor-default);
  /* stylelint-disable-next-line primer/borders */
  border-radius: 20px;
  animation: loadingCounterKeyFrames 1.2s ease-in-out infinite alternate;
}

@keyframes loadingCounterKeyFrames {
  from {
    opacity: 1;
  }

  to {
    opacity: 0.2;
  }
}
